import React, { useState, useEffect } from 'react';
import { Search, Filter, RefreshDouble, UserStar, Group } from 'iconoir-react';
import { useSearchProfiles, useExpressInterest } from '../../hooks/useDiscover';
import AnonymousProfileCard from '../../components/discover/AnonymousProfileCard';
import ReceivedInterests from '../../components/discover/ReceivedInterests';
import SentInterests from '../../components/discover/SentInterests';
import ProfileDetailModal from '../../components/discover/ProfileDetailModal';
import { Toaster } from '../../components/common';
import { Tour } from '../../components/common/Tour';
import { InfoTip } from '../../components/common/InfoTip';
import toast from 'react-hot-toast';
import type { SearchProfilesParams, AnonymousProfile } from '../../services/discoverService';

/**
 * Discover Page
 * Phase 3.3: Interactive Search/Browse
 */

// Jun-18 (a user V1 test, O3 follow-up): the "Urgency Level" filter was removed.
// Same root cause as the O3 badge fix (Jun-16): the AI service hardcodes
// urgency='ongoing' on every summary write and onboarding never asks a timeline
// question, so urgency is a fabricated universal default carrying zero signal.
// Filtering by it was meaningless and misleading (Urgent/Time Sensitive/Exploring
// match nothing; Ongoing matches everyone). The Jun-16 fix suppressed the badge
// but left this filter. Re-introduce only when onboarding captures a real
// timeline answer that maps to urgency.

const OBJECTIVE_OPTIONS = [
  { value: 'fundraising', label: 'Fundraising' },
  { value: 'hiring', label: 'Hiring / Recruiting' },
  { value: 'partnerships', label: 'Partnerships' },
  { value: 'mentorship', label: 'Mentorship' },
  { value: 'networking', label: 'General Networking' },
  { value: 'sales', label: 'Sales / Business Dev' },
  { value: 'advisory', label: 'Advisory / Board' },
  { value: 'investment', label: 'Investment' },
];

const INDUSTRY_OPTIONS = [
  { value: 'technology', label: 'Technology' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'fintech', label: 'FinTech' },
  { value: 'ecommerce', label: 'E-Commerce' },
  { value: 'saas', label: 'SaaS' },
  { value: 'ai_ml', label: 'AI / Machine Learning' },
  { value: 'biotech', label: 'Biotech' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'education', label: 'Education' },
  { value: 'media', label: 'Media / Entertainment' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'manufacturing', label: 'Manufacturing' },
];

const DiscoverPage: React.FC = () => {
  const [searchParams, setSearchParams] = useState<SearchProfilesParams>({
    page: 1,
    limit: 12,
  });
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AnonymousProfile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedObjectives, setSelectedObjectives] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

  const { data, isLoading, refetch } = useSearchProfiles(searchParams);
  const { mutate: expressInterest, isPending: isExpressingInterest } = useExpressInterest();
  const profiles = data?.result?.profiles || [];
  const total = data?.result?.total || 0;
  const totalPages = Math.ceil(total / (searchParams.limit || 12));

  // Debounced search - triggers 300ms after user stops typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchParams(prev => {
        // Only update if query actually changed
        const newQuery = searchInput.trim() || undefined;
        if (prev.query === newQuery) return prev;
        return {
          ...prev,
          query: newQuery,
          page: 1,
        };
      });
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Immediate search on form submit (Enter key)
    setSearchParams(prev => ({
      ...prev,
      query: searchInput.trim() || undefined,
      page: 1,
    }));
  };

  const handleObjectiveToggle = (objective: string) => {
    setSelectedObjectives(prev => {
      const updated = prev.includes(objective)
        ? prev.filter(o => o !== objective)
        : [...prev, objective];
      setSearchParams(p => ({
        ...p,
        objectives: updated.length > 0 ? updated : undefined,
        page: 1,
      }));
      return updated;
    });
  };

  const handleIndustryToggle = (industry: string) => {
    setSelectedIndustries(prev => {
      const updated = prev.includes(industry)
        ? prev.filter(i => i !== industry)
        : [...prev, industry];
      setSearchParams(p => ({
        ...p,
        industries: updated.length > 0 ? updated : undefined,
        page: 1,
      }));
      return updated;
    });
  };

  const clearFilters = () => {
    setSelectedObjectives([]);
    setSelectedIndustries([]);
    setSearchParams(prev => ({
      ...prev,
      objectives: undefined,
      industries: undefined,
      page: 1,
    }));
  };

  const handlePageChange = (newPage: number) => {
    // BUG-020 FIX: Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSearchParams(prev => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleViewProfile = (profile: AnonymousProfile) => {
    setSelectedProfile(profile);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProfile(null);
  };

  const handleExpressInterestFromModal = (profileId: string) => {
    expressInterest(
      { to_user_id: profileId },
      {
        onSuccess: (response: any) => {
          const matchCreated = response.result?.match_created;
          const message = matchCreated
            ? 'Mutual interest! A match has been created.'
            : 'Interest expressed! They will be notified.';
          toast.custom(t => (
            <Toaster message={message} type="success" id={t.id} />
          ));
          handleCloseModal();
        },
        onError: (error: any) => {
          toast.custom(t => (
            <Toaster
              message={error?.message || 'Failed to express interest'}
              type="error"
              id={t.id}
            />
          ));
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Tour name='discover' />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        {/* Apr-25: header carries an InfoTip explaining the
            anonymous-until-mutual mechanism. Page subtitle was vague about
            what "express interest" actually does: now there's a one-tap
            explanation right where the user lands. */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-heading-500 mb-2 inline-flex items-center gap-2">
            Discover Connections
            <InfoTip tooltipKey="discover-anonymous" />
          </h1>
          <p className="text-body-500">
            Browse anonymous profiles. Express interest: names are revealed only when both sides reciprocate.
          </p>
        </div>

        {/* Jun-16 (a user V1 test, D6): "see when someone connects with me" —
            surfaces incoming interest (anonymous until you connect back). */}
        <ReceivedInterests />

        {/* Jun-19 (a user V1 test): sender-side mirror — shows the user's own
            still-pending sent interests ("Waiting to hear back"). Without this,
            expressing interest on Discover was invisible to the sender (it is a
            connection_interests row, not a match, so it never appeared in the
            matches-only "Waiting on them" tab). */}
        <SentInterests />

        {/* Search and Filters */}
        <div data-tour-id="discover-search" className="bg-white rounded-xl p-4 mb-6 shadow-sm">
          <form onSubmit={handleSearch} className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-body-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search profiles: try 'fundraising', 'AI startup', 'mentor', 'healthcare'..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 border rounded-lg flex items-center gap-2 transition-colors ${
                showFilters ? 'border-primary-500 bg-primary-50 text-primary-600' : 'border-gray-200 text-body-500 hover:border-primary-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Search
            </button>
          </form>

          {/* Filters Panel */}
          {showFilters && (
            <div className="border-t border-gray-100 pt-4 space-y-4">
              {(selectedObjectives.length > 0 || selectedIndustries.length > 0) && (
                <div className="flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-500 hover:text-primary-600 underline"
                  >
                    Clear all filters
                  </button>
                </div>
              )}

              {/* Objectives Filter */}
              <div>
                <label className="block text-xs font-medium text-body-500 mb-2">
                  Looking For (Objectives)
                </label>
                <div className="flex flex-wrap gap-2">
                  {OBJECTIVE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => handleObjectiveToggle(opt.value)}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        selectedObjectives.includes(opt.value)
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-body-600 border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Industries Filter */}
              <div>
                <label className="block text-xs font-medium text-body-500 mb-2">
                  Industry
                </label>
                <div className="flex flex-wrap gap-2">
                  {INDUSTRY_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => handleIndustryToggle(opt.value)}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        selectedIndustries.includes(opt.value)
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-body-600 border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-body-500">
            {isLoading ? 'Searching...' : `${total} profiles found`}
          </p>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600"
          >
            <RefreshDouble className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Profile Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
                <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-4" />
                <div className="h-8 bg-gray-200 rounded w-full" />
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            {searchInput || selectedObjectives.length > 0 || selectedIndustries.length > 0 ? (
              <>
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-body-500 mb-2 font-medium">No profiles match your filters</p>
                <p className="text-sm text-body-400 mb-4">
                  Try adjusting your search criteria or clearing filters
                </p>
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium"
                >
                  Clear all filters
                </button>
              </>
            ) : (
              <>
                <Group className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                <p className="text-body-500 mb-2 font-medium">Discover new connections</p>
                <p className="text-sm text-body-400 max-w-sm mx-auto">
                  New professionals are joining every day. Your AI agent is constantly working to find your best matches. Check back soon!
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {profiles.map((profile: AnonymousProfile) => (
              <AnonymousProfileCard
                key={profile.id}
                profile={profile}
                onViewProfile={handleViewProfile}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <button
              onClick={() => handlePageChange(Math.max(1, (searchParams.page || 1) - 1))}
              disabled={(searchParams.page || 1) === 1}
              className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary-200"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-body-500">
              Page {searchParams.page || 1} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(Math.min(totalPages, (searchParams.page || 1) + 1))}
              disabled={(searchParams.page || 1) === totalPages}
              className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary-200"
            >
              Next
            </button>
          </div>
        )}

        {/* Profile Detail Modal */}
        <ProfileDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          profile={selectedProfile}
          onExpressInterest={handleExpressInterestFromModal}
          isExpressingInterest={isExpressingInterest}
        />
      </div>
    </div>
  );
};

export default DiscoverPage;
